import { useCallback, useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import {
  ContentAccessUser,
  UserRoles,
  WorkflowEventTypes,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';

import RouterPaths from '../../common/RouterPaths';
import { DRAFT_STATES } from '../../api/ContentUtil';
import { LoadedDocument } from '../../model/LoadedDocument';
import { createWorkflowEventWithStatePolling } from '../../api/CreateWorkflowEventWithStatePolling';
import { log } from '../../loggers/Logger';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useCurrentUser } from '../../contexts/UserContext';
import { useMplServiceClient } from '../../contexts/MplServiceClientContext';
import { useLoadedDocument } from '../v2-hooks/useLoadedDocument';

/** A user picked in the co-author search, pending addition. */
interface SelectedCoAuthor {
  readonly userId: string;
  readonly userName: string;
}

/** Presentation state and commands for the author "Actions" dropdown. */
export interface AuthorActionsViewModel {
  /** Whether the dropdown should render (author/co-author or privileged role). */
  readonly isVisible: boolean;
  /** Error from the last "Make updates" attempt, or `undefined`. */
  readonly actionError: string | undefined;
  /** True while "Make updates" is in flight. */
  readonly isProcessingAction: boolean;
  /** Claim the update (lock the draft) and open the editor. */
  readonly makeUpdates: () => void;
  /** Dismiss the "Make updates" error alert. */
  readonly dismissActionError: () => void;
  /** Whether the add-co-author modal is open. */
  readonly isCoAuthorModalOpen: boolean;
  /** Open the add-co-author modal. */
  readonly openCoAuthorModal: () => void;
  /** Close the add-co-author modal and clear its selection. */
  readonly closeCoAuthorModal: () => void;
  /** Record the user picked in the search control, validating the pick. */
  readonly selectCoAuthor: (userId: string, userName: string, validationError: string) => void;
  /** Whether a valid co-author is selected and ready to add. */
  readonly isCoAuthorValid: boolean;
  /** Validation or submission error for the co-author, or `undefined`. */
  readonly coAuthorError: string | undefined;
  /** True while the add-co-author call is in flight. */
  readonly isAddingCoAuthor: boolean;
  /** Add the selected user as a co-author. */
  readonly addCoAuthor: () => void;
}

/** Reject a pick that duplicates the owner or an existing co-author. */
function describeDuplicateCoAuthor(userId: string, document: LoadedDocument): string | undefined {
  if (document.author.userId === userId) {
    return 'User is already the content owner. Select a different user.';
  }
  if (document.coAuthors.some((coAuthor) => coAuthor.userId === userId)) {
    return 'Content is already shared with this user. Select a different user.';
  }
  return undefined;
}

/**
 * Drives the author "Actions" dropdown on the Update Requests tab: "Make
 * updates" (lock the draft, open the editor) and "Add co-author". Visible to
 * the content owner, co-authors, and privileged roles
 * (admin/tech-writer/bar-raiser).
 *
 * Returns undefined while no document handle is loaded — the view renders
 * nothing during that transient state rather than crashing.
 */
export function useAuthorActionsViewModel(): AuthorActionsViewModel | undefined {
  const editorService = useContentEditorService();
  const mpl = useMplServiceClient();
  const history = useHistory();
  const { userId, roles } = useCurrentUser();
  const document = useLoadedDocument();

  const contentId = document?.contentId;

  const [actionError, setActionError] = useState<string | undefined>(undefined);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [isCoAuthorModalOpen, setIsCoAuthorModalOpen] = useState(false);
  const [selectedCoAuthor, setSelectedCoAuthor] = useState<SelectedCoAuthor | undefined>(undefined);
  const [coAuthorError, setCoAuthorError] = useState<string | undefined>(undefined);
  const [isAddingCoAuthor, setIsAddingCoAuthor] = useState(false);

  const makeUpdates = useCallback(async () => {
    if (!contentId) return;
    setIsProcessingAction(true);
    setActionError(undefined);
    try {
      // Published docs carry no in-memory workflow; fetch it for lock/draft state.
      const { workflow } = await mpl.getContentWorkflow(contentId);

      // When the draft is editable and unlocked, lock it for the edit session.
      if (
        !workflow.isLocked &&
        DRAFT_STATES.includes(workflow.draftState) &&
        workflow.publicationState !== WorkflowState.FINALIZING
      ) {
        await createWorkflowEventWithStatePolling(
          contentId,
          workflow.draftWorkflowId,
          WorkflowEventTypes.LOCK,
          '',
        );
      }

      history.push(generatePath(RouterPaths.CONTENT_EDIT, { contentId }));
    } catch (err) {
      setActionError('Unable to start editing. Please try again later.');
      log.error({ err, contentId }, 'Failed to start content update');
    } finally {
      setIsProcessingAction(false);
    }
  }, [mpl, contentId, history]);

  const dismissActionError = useCallback(() => setActionError(undefined), []);

  const openCoAuthorModal = useCallback(() => {
    setIsCoAuthorModalOpen(true);
    setSelectedCoAuthor(undefined);
    setCoAuthorError(undefined);
  }, []);

  const closeCoAuthorModal = useCallback(() => {
    setIsCoAuthorModalOpen(false);
    setSelectedCoAuthor(undefined);
    setCoAuthorError(undefined);
  }, []);

  const selectCoAuthor = useCallback(
    (pickedUserId: string, pickedUserName: string, validationError: string) => {
      if (!document) return;

      // When the search control reports an error or clears, reject the pick.
      if (!pickedUserId || validationError) {
        setSelectedCoAuthor(undefined);
        setCoAuthorError(validationError || undefined);
        return;
      }

      // When the pick duplicates the owner or an existing co-author, reject it.
      const duplicateError = describeDuplicateCoAuthor(pickedUserId, document);
      if (duplicateError) {
        setSelectedCoAuthor(undefined);
        setCoAuthorError(duplicateError);
        return;
      }

      setSelectedCoAuthor({ userId: pickedUserId, userName: pickedUserName });
      setCoAuthorError(undefined);
    },
    [document],
  );

  const addCoAuthor = useCallback(async () => {
    if (!selectedCoAuthor) return;
    setIsAddingCoAuthor(true);
    setCoAuthorError(undefined);
    try {
      // Share the content with the selected user as a co-author.
      await editorService.addCoAuthor(
        ContentAccessUser.create(selectedCoAuthor.userId, selectedCoAuthor.userName, ''),
      );
      setIsCoAuthorModalOpen(false);
      setSelectedCoAuthor(undefined);
    } catch (err) {
      setCoAuthorError('Unable to add co-author. Please try again later.');
      log.error({ err, contentId }, 'Failed to add co-author');
    } finally {
      setIsAddingCoAuthor(false);
    }
  }, [selectedCoAuthor, contentId, editorService]);

  // A parent-teardown render can transiently clear the document before this
  // view unmounts; return undefined so the view renders null instead of throwing.
  if (!document) return undefined;

  // Owner or co-author of the loaded document. The Update Requests tab renders
  // on published content, which carries no draft workflow, so ownership comes
  // from the document rather than a workflow.
  const isAuthor =
    document.author.userId === userId ||
    document.coAuthors.some((coAuthor) => coAuthor.userId === userId);
  // Admin/tech-writer/bar-raiser (Publisher) may act like an author.
  const isPrivileged =
    roles.has(UserRoles.Admin) || roles.has(UserRoles.TechWriter) || roles.has(UserRoles.Publisher);
  const isVisible = isAuthor || isPrivileged;

  return {
    isVisible,
    actionError,
    isProcessingAction,
    makeUpdates,
    dismissActionError,
    isCoAuthorModalOpen,
    openCoAuthorModal,
    closeCoAuthorModal,
    selectCoAuthor,
    isCoAuthorValid: selectedCoAuthor !== undefined,
    coAuthorError,
    isAddingCoAuthor,
    addCoAuthor,
  };
}
